import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Tag, Button } from 'antd';
import { ClockCircleOutlined, FormOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

const colors = {
    primary: '#f79421',
    secondary: '#262262',
    white: '#fff',
};

const calcTimeLeft = (endDate) => {
    const diff = new Date(endDate).getTime() - Date.now();
    if (diff <= 0) return null;
    return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / 1000 / 60) % 60),
        seconds: Math.floor((diff / 1000) % 60),
    };
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('vi-VN') : '';

const TimeBox = ({ value, label, isMobile }) => (
    <div style={{
        background: colors.secondary,
        borderRadius: 8,
        padding: isMobile ? '10px 12px' : '16px 20px',
        minWidth: isMobile ? 60 : 80,
        textAlign: 'center',
    }}>
        <div style={{
            fontSize: isMobile ? 28 : 40,
            fontWeight: 700,
            color: colors.primary,
            lineHeight: 1,
        }}>
            {String(value).padStart(2, '0')}
        </div>
        <Text style={{ color: colors.white, fontSize: isMobile ? 11 : 13 }}>{label}</Text>
    </div>
);

// Card dùng chung cho các trạng thái không có CTA đăng ký (không có đợt / sắp mở / đã đóng)
const InfoCard = ({ isMobile, title, description, tagColor, tagText, disabledButtonText }) => (
    <div style={{
        padding: isMobile ? '20px 16px' : '40px 20px',
        background: `linear-gradient(135deg, ${colors.secondary} 0%, #3a357a 100%)`,
    }}>
        <Card
            style={{
                borderRadius: 12,
                border: `2px solid ${colors.primary}`,
                background: 'rgba(255,255,255,0.05)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            }}
            bodyStyle={{ padding: isMobile ? '16px 16px' : '28px 36px' }}
        >
            <Row align="middle" gutter={[16, 16]}>
                <Col xs={24} md={14}>
                    <Title level={isMobile ? 5 : 4} style={{ color: colors.white, margin: '0 0 6px' }}>
                        {title}
                    </Title>
                    {description && (
                        <Text style={{ color: colors.white, fontSize: 15 }}>
                            <ClockCircleOutlined style={{ marginRight: 4 }} />
                            {description}
                        </Text>
                    )}
                </Col>
                <Col xs={24} md={10} style={{ display: 'flex', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
                    {tagText ? (
                        <Tag color={tagColor} style={{ fontSize: 14, padding: '6px 16px' }}>
                            {tagText}
                        </Tag>
                    ) : null}
                    {disabledButtonText && (
                        <Button
                            type="default"
                            size="large"
                            disabled
                            style={{ borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.4)', background: 'transparent', marginLeft: 12 }}
                        >
                            {disabledButtonText}
                        </Button>
                    )}
                </Col>
            </Row>
        </Card>
    </div>
);

const AdmissionCountdown = ({ isMobile, period, loading }) => {
    const [timeLeft, setTimeLeft] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (period?.status !== 'active' || !period?.end_date) {
            setTimeLeft(null);
            return;
        }
        setTimeLeft(calcTimeLeft(period.end_date));
        const timer = setInterval(() => {
            setTimeLeft(calcTimeLeft(period.end_date));
        }, 1000);
        return () => clearInterval(timer);
    }, [period]);

    if (loading) return null;

    if (!period) {
        return (
            <InfoCard
                isMobile={isMobile}
                title="Hiện chưa có đợt tuyển sinh nào đang mở"
                description="Vui lòng quay lại sau."
                tagColor="default"
                tagText="Chưa có đợt tuyển sinh"
            />
        );
    }

    if (period.status === 'upcoming') {
        return (
            <InfoCard
                isMobile={isMobile}
                title="Đợt tuyển sinh sắp mở"
                description={period.start_date ? `Bắt đầu từ ngày ${formatDate(period.start_date)}` : ''}
                tagColor="blue"
                tagText="Sắp mở đăng ký"
            />
        );
    }

    if (period.status === 'closed' || !timeLeft) {
        return (
            <InfoCard
                isMobile={isMobile}
                title="Đã hết hạn đăng ký"
                description={period.end_date ? `Ngày ${formatDate(period.end_date)}` : ''}
                tagColor="red"
                tagText="Đã kết thúc nhận hồ sơ"
                disabledButtonText="Đã đóng nhận hồ sơ"
            />
        );
    }

    return (
        <div style={{
            padding: isMobile ? '20px 16px' : '40px 20px',
            background: `linear-gradient(135deg, ${colors.secondary} 0%, #3a357a 100%)`,
        }}>
            <Card
                style={{
                    borderRadius: 12,
                    border: `2px solid ${colors.primary}`,
                    background: 'rgba(255,255,255,0.05)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                }}
                bodyStyle={{ padding: isMobile ? '16px 16px' : '28px 36px' }}
            >
                <Row align="middle" gutter={[16, 16]}>
                    {/* Cột 1: Thông tin đợt */}
                    <Col xs={24} md={7}>
                        <Title level={isMobile ? 5 : 4} style={{ color: colors.white, margin: '0 0 6px' }}>
                            Hạn cuối đăng ký và cập nhật hồ sơ
                        </Title>
                        {period.start_date && period.end_date && (
                            <Text style={{ color: colors.white, fontSize: 15 }}>
                                <ClockCircleOutlined style={{ marginRight: 4 }} />
                                Ngày {formatDate(period.end_date)}
                            </Text>
                        )}
                    </Col>

                    {/* Cột 2: Đồng hồ đếm ngược */}
                    <Col xs={24} md={10}>
                        <Row gutter={6} align="middle" style={{ flexWrap: 'nowrap' }}>
                            <Col><TimeBox value={timeLeft.days} label="Ngày" isMobile={isMobile} /></Col>
                            <Col><span style={{ color: colors.primary, fontSize: isMobile ? 24 : 32, fontWeight: 700, lineHeight: 1 }}>:</span></Col>
                            <Col><TimeBox value={timeLeft.hours} label="Giờ" isMobile={isMobile} /></Col>
                            <Col><span style={{ color: colors.primary, fontSize: isMobile ? 24 : 32, fontWeight: 700, lineHeight: 1 }}>:</span></Col>
                            <Col><TimeBox value={timeLeft.minutes} label="Phút" isMobile={isMobile} /></Col>
                            <Col><span style={{ color: colors.primary, fontSize: isMobile ? 24 : 32, fontWeight: 700, lineHeight: 1 }}>:</span></Col>
                            <Col><TimeBox value={timeLeft.seconds} label="Giây" isMobile={isMobile} /></Col>
                        </Row>
                    </Col>

                    {/* Cột 3: CTA */}
                    <Col xs={24} md={7} style={{ display: 'flex', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'flex-start' : 'flex-end', gap: 10 }}>
                            <Button
                                type="primary"
                                size="large"
                                icon={<FormOutlined />}
                                onClick={() => navigate('/register')}
                                style={{
                                    background: colors.primary,
                                    borderColor: colors.primary,
                                    fontWeight: 700,
                                    height: 44,
                                    paddingInline: 24,
                                    boxShadow: '0 4px 16px rgba(247,148,33,0.5)',
                                }}
                            >
                                Đăng ký ngay <ArrowRightOutlined />
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default AdmissionCountdown;
